import { useQuery } from "@apollo/client";
import { Box, Flex } from "@chakra-ui/react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";

import {
  Alert,
  LoadingIndicator,
  PageContent,
  PageTitle,
} from "../../../components";
import { useAiInterviewerEnabled } from "../../../hooks/useAiInterviewerEnabled";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { PageOptions } from "../../../hooks/useOffsetPagination";
import { usePageTracker } from "../../../utils/googleAnalytics";
import { AiInterviewerMetricsCards } from "../../components/AiInterviewersList/AiInterviewerMetricsCards";
import AiInterviewersList from "../../components/AiInterviewersList/AiInterviewersList";
import {
  AiInterviewerListSort,
  AiInterviewersFilterOptionsDocument,
  AiInterviewersPaginatedDocument,
  AiInterviewersTotalCountDocument,
  SortDirection,
} from "../../graphql";
import {
  REQUIRES_BH_SCREEN_MANAGE_PERMISSION,
  useBHScreenPermission,
} from "../../hooks/useBHScreenPermission";
import { DefaultLayout } from "../../layouts";
import NotFound from "../not-found/NotFoundPage";
import { AiInterviewersFilters } from "./AiInterviewersFilters";
import { CreateAiInterviewerButton } from "./CreateAiInterviewerButton";
import { useAiInterviewersFilters } from "./useAiInterviewersFilters";

const sortByToServerSort = (
  sortBy: SortingRule<string>
): { sort: AiInterviewerListSort; direction: SortDirection } => {
  if (sortBy.id === "status") {
    return {
      sort: "STATUS" as AiInterviewerListSort,
      direction: sortBy.desc ? SortDirection.Desc : SortDirection.Asc,
    };
  }

  if (sortBy.id === "interviewsCount") {
    return {
      sort: "INTERVIEWS_COUNT" as AiInterviewerListSort,
      direction: sortBy.desc ? SortDirection.Desc : SortDirection.Asc,
    };
  }

  if (sortBy.id === "currentVersion.createdBy.fullName") {
    return {
      sort: "AUTHOR" as AiInterviewerListSort,
      direction: sortBy.desc ? SortDirection.Desc : SortDirection.Asc,
    };
  }

  if (sortBy.id === "createdAt") {
    return {
      sort: "CREATED_AT" as AiInterviewerListSort,
      direction: sortBy.desc ? SortDirection.Desc : SortDirection.Asc,
    };
  }

  return {
    sort: "NAME" as AiInterviewerListSort,
    direction: sortBy.desc ? SortDirection.Desc : SortDirection.Asc,
  };
};

const AiInterviewersPage: React.FC = () => {
  usePageTracker("ai-interviewers");

  const [searchParams, setSearchParams] = useSearchParams();
  const featureEnabled = useAiInterviewerEnabled();
  const { canManage } = useBHScreenPermission();
  const isSmallScreen = useIsSmallScreen();

  const filters = useAiInterviewersFilters({
    searchParams,
    setSearchParams,
  });

  const [sortBy, setSortBy] = React.useState<SortingRule<string>>({
    id: "name",
    desc: false,
  });
  const { sort, direction } = React.useMemo(
    () => sortByToServerSort(sortBy),
    [sortBy]
  );

  const pageSize = Number(filters.itemsToShow);

  const { data, loading, error } = useQuery(AiInterviewersPaginatedDocument, {
    skip: !featureEnabled,
    fetchPolicy: "cache-and-network",
    variables: {
      statuses: filters.statusFilters.length
        ? filters.statusFilters
        : undefined,
      positionIds: filters.positionIdFilters.length
        ? filters.positionIdFilters
        : undefined,
      authorIds: filters.authorIdFilters.length
        ? filters.authorIdFilters
        : undefined,
      createdDateRange: filters.createdDateFilter || undefined,
      keyword: filters.debouncedSearchTerm.trim() || undefined,
      sort,
      direction,
      pagination: {
        page: filters.currentPage,
        limit: pageSize,
      },
    },
  });

  const { data: unfilteredTotalData } = useQuery(
    AiInterviewersTotalCountDocument,
    {
      skip: !featureEnabled,
      fetchPolicy: "cache-first",
      variables: {
        pagination: {
          page: 1,
          limit: 1,
        },
      },
    }
  );

  const { data: filterOptionsData } = useQuery(
    AiInterviewersFilterOptionsDocument,
    {
      skip: !featureEnabled,
      fetchPolicy: "cache-first",
    }
  );

  const paginatedAiInterviewers = data?.aiInterviewersPaginated.results ?? [];
  const totalPages = data?.aiInterviewersPaginated.pageInfo?.totalPages ?? 0;
  const totalCount = data?.aiInterviewersPaginated.pageInfo?.totalRows ?? 0;
  const totalCountWithoutFilters =
    unfilteredTotalData?.aiInterviewersPaginated.pageInfo?.totalRows ??
    totalCount;
  const allAiInterviewers = filterOptionsData?.aiInterviewers ?? [];

  React.useEffect(() => {
    if (totalPages > 0 && filters.currentPage > totalPages) {
      filters.setCurrentPage(totalPages);
    }
  }, [filters.currentPage, filters.setCurrentPage, totalPages]);

  const pageOptions: PageOptions = React.useMemo(
    () => ({
      currentPage: filters.currentPage,
      totalPages,
      handlePageNumberChange: filters.setCurrentPage,
      sortBy,
      onChangeSort: (nextSort) => {
        if (nextSort.length < 1) {
          return;
        }

        const [nextSortRule] = nextSort;
        if (
          nextSortRule.id === sortBy.id &&
          Boolean(nextSortRule.desc) === Boolean(sortBy.desc)
        ) {
          return;
        }

        setSortBy(nextSortRule);
        filters.setCurrentPage(1);
      },
    }),
    [filters.currentPage, filters.setCurrentPage, sortBy, totalPages]
  );

  const disabledReason = !canManage
    ? REQUIRES_BH_SCREEN_MANAGE_PERMISSION
    : undefined;

  if (!featureEnabled) {
    return <NotFound />;
  }

  return (
    <DefaultLayout>
      <PageContent>
        <Box maxW="1160px" mx="auto">
          <Flex
            direction={{ base: "column", md: "row" }}
            justifyContent="space-between"
          >
            <PageTitle
              mb={{ base: 5, md: 7 }}
              titleText="Screen: AI Interviewers"
            />
            {!isSmallScreen && (
              <CreateAiInterviewerButton disabledReason={disabledReason} />
            )}
          </Flex>

          <AiInterviewerMetricsCards />

          <AiInterviewersFilters
            filters={filters}
            allAiInterviewers={allAiInterviewers}
            displayedCount={totalCount}
            totalCountWithoutFilters={totalCountWithoutFilters}
            isSmallScreen={isSmallScreen}
            loading={loading}
          />

          {error ? (
            <Alert
              status="error"
              title="Error loading AI Interviewers"
              description={error.message}
            />
          ) : loading ? (
            <LoadingIndicator />
          ) : (
            <AiInterviewersList
              aiInterviewers={paginatedAiInterviewers}
              hasFiltersApplied={filters.hasFiltersApplied}
              pageOptions={pageOptions}
            />
          )}
        </Box>
      </PageContent>
    </DefaultLayout>
  );
};

export default AiInterviewersPage;
